import type { DependencyList } from 'react';
import { useCallback, useEffect, useRef } from 'react';

type ClearMethodType = () => void;

interface DebounceOptions {
  timeout: number;
  trailingOnly: boolean;
}

const defaultOptions = {
  timeout: 300,
  trailingOnly: false,
};

export const useDebounce = (
  callback: () => void,
  dependencies: DependencyList = [],
  options?: Partial<DebounceOptions>,
): ClearMethodType => {
  const { timeout, trailingOnly } = { ...defaultOptions, ...options };
  const timeoutRef = useRef<{ timeout: ReturnType<typeof setTimeout> | null }>({ timeout: null });
  const firstRunRef = useRef(true);

  const clear = useCallback(() => {
    if (timeoutRef.current?.timeout) {
      clearTimeout(timeoutRef.current.timeout);
      timeoutRef.current.timeout = null;
    }
  }, [timeoutRef]);

  useEffect(() => {
    if (!trailingOnly || !firstRunRef.current) {
      clear();
      timeoutRef.current.timeout = setTimeout(callback, timeout);
    }
    firstRunRef.current = false;
    return clear;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, dependencies);

  return clear;
};
